import { FC, useEffect, useState } from 'react';
import { Box, Chip } from '@mui/material';

import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Dropdown from '@/components/Dropdown/Dropdown';
import Header from '@/components/Header/Header';
import Input from '@/components/Input/Input';
import StoreItemTable from '@/components/StoreProductsTable/StoreItemTable';
import StoreItemTablePlaceholder from '@/components/StoreProductsTable/StoreItemTablePlaceHolder';
import { useSTKContext } from '@/context/context';
import { useTranslation } from 'react-i18next';
import { useProductCategories, useStoreMenuDetails, useUpdateProductPrice } from '@/services/menu/menus.service';
import { useCurrentUser } from '@/services/user/user.service';
import { UserRoles } from '@/types/auth.types';
import { MenuDetails, Product, UpdateProduct } from '@/types/menus.types';

import styles from './AdminStoreMenuDetails.module.css';
import ModalStories from './internal/ModalStories.module';

const DEFAULT_CATEGORY_NAME = 'Pizza';

export const getStoreFilteredProducts = (prods: Product[], productDescription: string): Product[] =>
  prods.filter((prod) => {
    const descriptionMatches =
      prod.description.toUpperCase().includes(productDescription.toUpperCase()) ||
      prod.productName.toUpperCase().includes(productDescription.toUpperCase());
    return descriptionMatches;
  });

const AdminStoreMenuDetails: FC = () => {
  const { t } = useTranslation();
  const { stores } = useSTKContext();

  const [filterValues, setFilterValues] = useState({ categoryName: DEFAULT_CATEGORY_NAME, productDescription: '' });
  const [productDescriptionToFilter, setProductDescriptionToFilter] = useState('');
  const [filteredStories, setFilteredStories] = useState<MenuDetails[] | undefined>([]);
  const [modalOpen, setModalOpen] = useState(false);

  const {
    data: storeData,
    isLoading: isStoreLoading,
    isError: isStoreError,
    refetch,
  } = useStoreMenuDetails(true, stores);

  const { data: categories = [], isLoading: isLoadingCategories } = useProductCategories(true, stores, []);
  const { mutateAsync: updateProductPrice } = useUpdateProductPrice();

  const { data: userData } = useCurrentUser(true);

  const userCanEdit = userData?.data.role === UserRoles.EDIT;
  const productCategories = storeData?.data || [];

  const products = categories.map((productCategory) => ({
    value: productCategory.category,
    label: productCategory.category,
  }));

  const applyfilter = (catName: string, description: string) => {
    const newLocal = productCategories?.filter(
      (store) => store.categories.filter((category) => category.categoryName === catName).length > 0,
    );
    setProductDescriptionToFilter(description);
    setFilteredStories(newLocal);
  };

  // DEPRECATED
  const saveProduct = (storeNumber: string, product: Product) => {
    // const updateProductPriceRequest: UpdateProduct = {
    //   storeNo: storeNumber,
    //   variantPrices: [{ variantId: product.variantId, price: product.chosenPrice }],
    // };
    // updateProductPrice([updateProductPriceRequest]);

    setTimeout(refetch, 1000);
  };

  useEffect(() => {
    applyfilter(filterValues.categoryName, filterValues.productDescription);
  }, [isStoreLoading, storeData]);
  return (
    <div className={styles.page} data-testid="fzStoreMenu">
      <div>
        <div style={{ minWidth: 'fit-content' }}>
          <Header title={t('menuDetails.title')} breadcrumb={{ label: t('breadcrumb.backToAllMenus'), to: '/menus' }} />
        </div>
        <div className={styles.chip}>
          <Chip
            data-testid="icon"
            key="storeCount"
            label={t('menuDetails.storeLabel', { count: filteredStories?.length ?? 1 })}
            color="default"
            variant="outlined"
            onClick={() => setModalOpen(!modalOpen)}
            sx={{
              fontSize: '14px',
              fontWeight: '500',
              height: '28px',
              backgroundColor: 'white',
              color: '#266DCC',
              borderColor: '#266DCC',
              marginLeft: '160px',
              marginTop: '-135px',
            }}
          />
        </div>
      </div>
      <div className={styles.header}>Filter Products</div>
      <div className={styles.filterActions}>
        <div className={styles.dropdownContainer}>
          <Dropdown
            label={t('common.category')}
            id="category"
            options={products}
            loading={isLoadingCategories}
            hidePlaceholder={false}
            value={filterValues.categoryName}
            onChange={(e) =>
              setFilterValues((filterState) => ({
                ...filterState,
                categoryName: e.target.value,
              }))
            }
          />
        </div>
        <div className={styles.inputTextContainer}>
          <Input
            label={t('common.product')}
            value={filterValues.productDescription}
            id="product"
            helpText={t('common.optionalLabel')}
            onChange={(e) =>
              setFilterValues((filterState) => ({
                ...filterState,
                productDescription: e.target.value,
              }))
            }
          />
        </div>
        <div className={styles.filterButtonStyles}>
          <Button
            variant={ButtonVariants.Secondary}
            onClick={() => applyfilter(filterValues.categoryName, filterValues.productDescription)}
          >
            {t('common.applyFiltersButton')}
          </Button>
        </div>
        <div className={styles.filterButtonStyles}>
          <Button
            variant={ButtonVariants.Text}
            onClick={() => {
              setFilterValues({ categoryName: DEFAULT_CATEGORY_NAME, productDescription: '' });
              applyfilter(DEFAULT_CATEGORY_NAME, '');
            }}
          >
            {t('common.clearFiltersButton')}
          </Button>
        </div>
      </div>
      <Box bgcolor="var(--black-100)">
        <Box pl={{ sm: 4, md: 7 }} pr={6}>
          {isStoreLoading &&
            stores.map((store) => (
              <StoreItemTablePlaceholder key={store} storeNumber={store} loading={isStoreLoading} />
            ))}
          {filteredStories?.map((store) => (
            <StoreItemTable
              key={store.storeNumber}
              store={store}
              products={getStoreFilteredProducts(
                store.categories
                  .filter((category) => category.categoryName === filterValues.categoryName)
                  .flatMap((category) => category.products),
                productDescriptionToFilter,
              )}
              userType="Admin"
              editable={userCanEdit}
              onSaveProduct={saveProduct}
              loadingData={isStoreLoading}
            />
          ))}
        </Box>
      </Box>

      {modalOpen && <ModalStories stores={stores} isOpen={modalOpen} onClose={() => setModalOpen(false)} />}
    </div>
  );
};

export default AdminStoreMenuDetails;
